export const CAREER_SPONSORSHIP_LEAD_TITLE = 'Career Sponsorship Enquiry';
export const CAREER_SPONSORSHIP_LEAD_TYPE = 'career_sponsorship_builder';

export type CareerSponsorshipLeadPreferences = {
  lead_type?: string;
  career_sponsorship?: boolean;
  campaign_source?: string;
  source_page?: string;
  builder_goal?: string | null;
  builder_goal_label?: string | null;
  builder_background?: string | null;
  builder_background_label?: string | null;
  builder_timeline?: string | null;
  builder_timeline_label?: string | null;
  builder_programme_id?: string | null;
  builder_programme_title?: string | null;
  builder_payable_total?: number | null;
  builder_separate_total?: number | null;
  builder_sponsorship_percent?: number | null;
  builder_is_pathway_floor?: boolean;
  builder_step?: string | null;
  builder_courses?: Array<{
    course_id?: string;
    title?: string;
    href?: string;
    selected_date_id?: string | null;
    selected_date_label?: string | null;
  }> | null;
  builder_saved_at?: string | null;
};

type DetailRow = {
  label: string;
  value: string;
  multiline?: boolean;
};

const formatMoney = (value?: number | null) => {
  if (value == null || Number.isNaN(Number(value))) {
    return null;
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(Number(value));
};

const formatStep = (value?: string | null) => {
  if (!value) {
    return null;
  }

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const isCareerSponsorshipLead = (lead?: {
  metadata?: {
    title?: string;
    preferences?: CareerSponsorshipLeadPreferences;
  };
}) => {
  const preferences = lead?.metadata?.preferences;
  return (
    lead?.metadata?.title === CAREER_SPONSORSHIP_LEAD_TITLE ||
    preferences?.lead_type === CAREER_SPONSORSHIP_LEAD_TYPE ||
    preferences?.career_sponsorship === true
  );
};

export const getCareerSponsorshipPreferencesFromLead = (lead?: {
  metadata?: { preferences?: CareerSponsorshipLeadPreferences };
}) => lead?.metadata?.preferences ?? null;

export const buildCareerSponsorshipDetailRows = (
  preferences: CareerSponsorshipLeadPreferences | null,
): DetailRow[] => {
  if (!preferences) {
    return [];
  }

  const rows: DetailRow[] = [];
  const push = (label: string, value?: string | null, multiline = false) => {
    if (!value) {
      return;
    }
    rows.push({ label, value, multiline });
  };

  push('Programme', preferences.builder_programme_title || null);
  push(
    'Career goal',
    preferences.builder_goal_label || preferences.builder_goal || null,
  );
  push(
    'Background',
    preferences.builder_background_label ||
      preferences.builder_background ||
      null,
  );
  push(
    'Timeline',
    preferences.builder_timeline_label || preferences.builder_timeline || null,
  );
  push('Package price', formatMoney(preferences.builder_payable_total));
  push('Standalone total', formatMoney(preferences.builder_separate_total));

  if (preferences.builder_sponsorship_percent != null) {
    push('Sponsorship', `${preferences.builder_sponsorship_percent}%`);
  }

  if (preferences.builder_is_pathway_floor) {
    push('Pricing', 'Complete pathway package floor');
  }

  push('Builder step', formatStep(preferences.builder_step));

  const courseLines = (preferences.builder_courses || [])
    .map((course, index) => {
      if (!course?.title) {
        return null;
      }
      const date = course.selected_date_label
        ? ` - ${course.selected_date_label}`
        : '';
      return `${index + 1}. ${course.title}${date}`;
    })
    .filter(Boolean);

  if (courseLines.length > 0) {
    push('Selected courses', courseLines.join('\n'), true);
  }

  push('Source page', preferences.source_page || null);

  return rows;
};

export const getCareerSponsorshipLeadListSummary = (
  preferences: CareerSponsorshipLeadPreferences | null,
) => {
  if (!preferences) {
    return null;
  }

  return (
    [
      preferences.builder_programme_title,
      preferences.builder_goal_label || preferences.builder_goal,
      preferences.builder_sponsorship_percent != null
        ? `${preferences.builder_sponsorship_percent}% sponsorship`
        : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'Career sponsorship builder'
  );
};
